import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Search, RefreshCw } from 'lucide-react';
import { apiService } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const Container = styled.div`
  padding: 24px;
`;

const Card = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  min-width: 220px;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  min-width: 220px;
`;

const Button = styled.button`
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TopScroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: 12px;
  margin-top: 12px;
`;

const TopScrollInner = styled.div`
  height: 1px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;
`;

const ProdutoTh = styled(Th)`
  width: 360px;
  min-width: 360px;
  max-width: 360px;
  overflow: hidden;
`;

const ProdutoTd = styled(Td)`
  width: 360px;
  min-width: 360px;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ControleTh = styled(Th)`
  width: 60px;
  min-width: 60px;
  max-width: 60px;
`;

const ControleTd = styled(Td)`
  width: 60px;
  min-width: 60px;
  max-width: 60px;
`;

const PrecoTd = styled(Td)`
  white-space: normal;
  line-height: 1.2;
`;

const LabelMini = styled.div`
  font-size: 10px;
  color: #6b7280;
  line-height: 1.1;
`;

const ValorLinha = styled.div`
  font-size: 12px;
  color: #111827;
  line-height: 1.2;
`;

const Info = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

type FilialInfo = { id: number; nome: string; alias?: string | null };
type ProdutoRow = { Controle: number; Produto: string; precos: Record<string, { preco: number | null; quantidade: number | null }> };
type GrupoInfo = { Controle: number; Grupo: string };

const formatMoeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatQuantidade = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const aplicarPercentual = (v: number, perc: number) => v * (1 + perc);

const ProdutosPorFilial: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [grupo, setGrupo] = useState<string>('todos');
  const [grupos, setGrupos] = useState<GrupoInfo[]>([]);
  const [initReady, setInitReady] = useState(false);
  const [filiais, setFiliais] = useState<FilialInfo[]>([]);
  const [produtos, setProdutos] = useState<ProdutoRow[]>([]);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [topScrollWidth, setTopScrollWidth] = useState(0);
  const limit = 100;

  const buscar = async (reset: boolean = true) => {
    if (!isAuthenticated) return;
    if (reset) {
      setLoading(true);
      setOffset(0);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const currentOffset = reset ? 0 : offset;
      const res = await apiService.listarProdutosPorFilial(busca || undefined, grupo, limit, currentOffset);
      const listaFiliais = res.filiais || [];
      const listaProdutos = res.produtos || [];
      const proximoOffset = currentOffset + listaProdutos.length;
      if (reset) {
        setFiliais(listaFiliais);
        setProdutos(listaProdutos);
      } else {
        setProdutos((prev) => [...prev, ...listaProdutos]);
      }
      setHasMore(Boolean(res.hasMore));
      setOffset(proximoOffset);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao buscar produtos');
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const colunasFiliais = useMemo(() => filiais, [filiais]);
  const renderTituloFilial = (nome: string) => {
    const idx = nome.indexOf(' ');
    if (idx === -1) return nome;
    return (
      <>
        {nome.slice(0, idx)}
        <br />
        {nome.slice(idx + 1)}
      </>
    );
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const carregarGrupos = async () => {
      try {
        const filiaisLista = await apiService.listarFiliais();
        const primeira = filiaisLista?.[0];
        if (!primeira?.id) {
          setGrupos([]);
          setGrupo('todos');
          setInitReady(true);
          return;
        }
        const ref = String(primeira.id).startsWith('filial_') ? String(primeira.id) : `filial_${primeira.id}`;
        const lista = await apiService.listarGrupos(ref);
        const gruposLista = lista || [];
        setGrupos(gruposLista);
        const pneu = gruposLista.find((g) => String(g.Grupo || '').toUpperCase().includes('PNEUS'));
        if (pneu) {
          setGrupo(String(pneu.Controle));
        } else {
          setGrupo('todos');
        }
      } catch {
        setGrupos([]);
        setGrupo('todos');
      } finally {
        setInitReady(true);
      }
    };
    carregarGrupos();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!initReady) return;
    buscar(true);
  }, [grupo, initReady, isAuthenticated]);

  useEffect(() => {
    const syncWidth = () => {
      if (!tableWrapperRef.current) return;
      setTopScrollWidth(tableWrapperRef.current.scrollWidth);
    };
    const raf = requestAnimationFrame(syncWidth);
    return () => cancelAnimationFrame(raf);
  }, [produtos, colunasFiliais]);

  useEffect(() => {
    if (!hasMore) return;
    if (!sentinelRef.current) return;
    const target = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loading && !loadingMore) {
          buscar(false);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, busca, offset]);

  return (
    <Container>
      <Card>
        <Title>Produtos por filial</Title>
        <Row>
          <Input
            placeholder="Buscar por nome ou controle"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') buscar(true);
            }}
          />
          <Select value={grupo} onChange={(e) => setGrupo(e.target.value)}>
            <option value="todos">Todos</option>
            {grupos.map((g) => (
              <option key={g.Controle} value={String(g.Controle)}>
                {g.Grupo}
              </option>
            ))}
          </Select>
          <Button onClick={() => buscar(true)} disabled={loading}>
            <Search size={16} /> Buscar
          </Button>
          <Button onClick={() => buscar(true)} disabled={loading}>
            <RefreshCw size={16} /> Atualizar
          </Button>
          <Info>
            {produtos.length} produtos • {colunasFiliais.length} filiais
          </Info>
        </Row>

        <TopScroll
          ref={topScrollRef}
          onScroll={(e) => {
            if (tableWrapperRef.current) {
              tableWrapperRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <TopScrollInner style={{ width: topScrollWidth || '100%' }} />
        </TopScroll>

        <TableWrapper
          ref={tableWrapperRef}
          onScroll={(e) => {
            if (topScrollRef.current) {
              topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <Table>
            <thead>
              <tr>
                <ControleTh>Controle</ControleTh>
                <ProdutoTh>Produto</ProdutoTh>
                {colunasFiliais.map((f) => (
                  <Th key={f.id}>{renderTituloFilial(f.nome)}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan={2 + colunasFiliais.length}>Carregando...</Td>
                </tr>
              ) : produtos.length === 0 ? (
                <tr>
                  <Td colSpan={2 + colunasFiliais.length}>Nenhum produto encontrado</Td>
                </tr>
              ) : (
                produtos.map((p) => (
                  <tr key={p.Controle}>
                    <ControleTd>{p.Controle}</ControleTd>
                    <ProdutoTd>{p.Produto}</ProdutoTd>
                    {colunasFiliais.map((f) => {
                      const info = p.precos?.[String(f.id)];
                      const quantidadeNum = typeof info?.quantidade === 'number' ? info.quantidade : null;
                      const precoNum = typeof info?.preco === 'number' ? info.preco : null;
                      const quantidade = quantidadeNum !== null ? formatQuantidade(quantidadeNum) : '-';
                      const preco = precoNum !== null ? formatMoeda(precoNum) : '-';
                      const preco1a5 = precoNum !== null ? formatMoeda(aplicarPercentual(precoNum, 0.05)) : '-';
                      const preco6a12 = precoNum !== null ? formatMoeda(aplicarPercentual(precoNum, 0.13)) : '-';
                      const precoRetirada = precoNum !== null ? formatMoeda(aplicarPercentual(precoNum, 0.30)) : '-';
                      return (
                        <PrecoTd key={`${p.Controle}-${f.id}`}>
                          <LabelMini>Qtd</LabelMini>
                          <ValorLinha>{quantidade}</ValorLinha>
                          <LabelMini>à vista</LabelMini>
                          <ValorLinha>{preco}</ValorLinha>
                          <LabelMini>1 a 5x</LabelMini>
                          <ValorLinha>{preco1a5}</ValorLinha>
                          <LabelMini>6 a 12x</LabelMini>
                          <ValorLinha>{preco6a12}</ValorLinha>
                          <LabelMini>Retirada</LabelMini>
                          <ValorLinha>{precoRetirada}</ValorLinha>
                        </PrecoTd>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrapper>
        <div ref={sentinelRef} />
        {(loadingMore || hasMore) && (
          <Info>{loadingMore ? 'Carregando mais...' : 'Role para carregar mais'}</Info>
        )}
      </Card>
    </Container>
  );
};

export default ProdutosPorFilial;
